import { QueryFunction } from '@tanstack/react-query';
import { Loan } from './types';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';
import { MosError } from 'src/utils/errors/MosError';

type LoanSearchByNumberRow = {
  loanId: string;
  loanNumber?: string | null;
} & Record<string, unknown>;

type GetLoansByLoanNumberResponse = {
  success?: boolean;
  /** Legacy OpenAPI shape */
  loans?: Loan[];
  /** Current search endpoint shape */
  rows?: LoanSearchByNumberRow[];
};

const mapRowsToLoans = (rows: LoanSearchByNumberRow[]): Loan[] =>
  rows.map(
    (row) =>
      ({
        id: row.loanId,
        loanNumber: String(row.loanNumber ?? ''),
      }) as Loan,
  );

const getLoansByLoanNumber: QueryFunction<
  Loan[],
  ['/loans/search/{loanNumber}', { loanNumber: string; limit: number }]
> = async ({ queryKey }) => {
  const [, params] = queryKey;
  const { loanNumber, limit } = params;

  const queryParams = new URLSearchParams();
  queryParams.append('limit', limit.toString());
  const url = `/loans/search/${loanNumber}?${queryParams.toString()}`;

  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<GetLoansByLoanNumberResponse>(url, {
    method: 'GET',
  });

  if (!response.success || !response.data) {
    throw new MosError({
      message:
        'Failed to fetch loans by loan number for loan number: ' + loanNumber,
      statusCode: response.status,
    });
  }

  // Prefer current search shape ({ rows }); fall back to legacy ({ loans }).
  // Always return an array — React Query v5 rejects undefined and callers use .map.
  if (Array.isArray(response.data.rows)) {
    return mapRowsToLoans(response.data.rows);
  }
  if (Array.isArray(response.data.loans)) {
    return response.data.loans;
  }

  return [];
};

export default getLoansByLoanNumber;
